import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, CheckCircle2, Circle, Lightbulb, AlertTriangle,
  FileText, BookOpen, Target, Info, Compass, ListChecks, Pencil, Download,
} from "lucide-react";
import api from "@/lib/api";
import { downloadTemplate } from "@/lib/download";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import RichBody from "@/components/RichBody";
import { Overline, Loading, LockCard } from "@/components/shared";

export default function Chapter() {
  const { slug } = useParams();
  const { setPart2 } = useTheme();
  const { user } = useAuth();
  const [ch, setCh] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setCh(null);
    api.get(`/content/chapters/${slug}`).then((r) => {
      setCh(r.data);
      setCompleted(r.data.progress?.completed || false);
      setPart2(r.data.part === "part_2");
    });
  }, [slug, setPart2]);
  useEffect(() => { load(); window.scrollTo(0, 0); }, [load]);
  useEffect(() => () => setPart2(false), [setPart2]);

  if (!ch) return <Loading />;

  const toggleComplete = async () => {
    const next = !completed;
    setCompleted(next);
    try {
      await api.post("/progress/complete", { chapter_slug: slug, completed: next });
      toast.success(next ? "Marked complete" : "Marked incomplete");
    } catch { setCompleted(!next); toast.error("Could not save progress"); }
  };

  const handleDownload = async (t) => {
    setBusy(t.slug);
    await downloadTemplate(t.slug, t.title);
    setBusy(null);
  };

  if (!ch.unlocked) {
    return (
      <div className="max-w-3xl" data-testid="chapter-locked">
        <BackLink part={ch.part} />
        <Overline className={ch.part === "part_2" ? "text-nm-green" : ""}>{ch.section_label}</Overline>
        <h1 className={`font-serif text-4xl sm:text-5xl mt-2 leading-tight ${ch.part === "part_2" ? "text-nm-plum" : "text-nm-ink"}`}>{ch.title}</h1>
        <p className="text-lg text-nm-neutral font-light mt-4 leading-relaxed">{ch.summary}</p>
        <div className="mt-8">
          <LockCard part={ch.part} teaser={ch.locked_teaser_copy || "Unlock this part to read the full chapter."}
            points={ch.preview?.preview_points || []} />
        </div>
      </div>
    );
  }

  const b = ch.content_blocks;
  const isP2 = ch.part === "part_2";
  const T = isP2
    ? { overline: "text-nm-green", accent: "text-nm-plum", accent50: "text-nm-plum/50",
        dot: "bg-nm-plum", primaryBtn: "bg-nm-plum", borderSoft: "border-nm-plum/10",
        borderHover: "hover:border-nm-plum/25", tint: "border-nm-plum/12 bg-nm-plum/[0.03]",
        startBox: "border-nm-green/35 bg-nm-green/[0.08]", startIcon: "text-nm-green",
        simpleBox: "border-nm-plum/25 bg-nm-plum/[0.05]", simpleIcon: "text-nm-plum",
        backHover: "hover:text-nm-plum", border10: "border-nm-plum/10", sectionRule: "border-white",
        headerAccent: "text-nm-plum", headerBar: "bg-nm-plum" }
    : { overline: "text-nm-coral", accent: "text-nm-primary", accent50: "text-nm-primary/50",
        dot: "bg-nm-primary", primaryBtn: "bg-nm-primary", borderSoft: "border-nm-primary/10",
        borderHover: "hover:border-nm-primary/25", tint: "border-nm-primary/12 bg-nm-primary/[0.03]",
        startBox: "border-nm-highlight/30 bg-gradient-to-b from-[#FCF6E4] to-[#FFFDF8]", startIcon: "text-nm-coral",
        simpleBox: "border-nm-green/25 bg-nm-green/[0.06]", simpleIcon: "text-nm-green",
        backHover: "hover:text-nm-primary", border10: "border-nm-primary/10", sectionRule: "border-white",
        headerAccent: "text-nm-coral", headerBar: "bg-nm-coral" };

  return (
    <div className="grid lg:grid-cols-[1fr_280px] gap-12" data-testid="chapter-page">
      <article className="max-w-none min-w-0">
        <div className="flex items-center justify-between gap-3">
          <BackLink part={ch.part} />
          {user?.is_admin && (
            <Link to={`/app/admin/chapter/${slug}`} data-testid="admin-edit-chapter"
              className="inline-flex items-center gap-1.5 rounded-full border border-nm-coral/40 px-4 py-1.5 text-xs font-medium text-nm-coral hover:bg-nm-coral/10 transition-colors">
              <Pencil size={13} /> Edit chapter
            </Link>
          )}
        </div>
        <Overline className={T.overline}>{b.section_label}</Overline>
        <h1 className={`font-serif text-4xl sm:text-5xl mt-2 leading-tight ${isP2 ? "text-nm-plum" : "text-nm-ink"}`}>{ch.title}</h1>
        <p className="text-xl text-nm-neutral font-light leading-relaxed mt-5">{ch.summary}</p>

        {b.body ? <RichBody blocks={b.body} T={T} /> : (<>
        {b.start_here && (
          <div className={`mt-8 rounded-2xl border ${T.startBox} p-6`} data-testid="start-here-box">
            <div className="flex items-center gap-2 text-nm-ink font-medium"><Compass size={17} className={T.startIcon} /> Start here</div>
            <p className="text-nm-ink/80 leading-relaxed mt-2">{b.start_here}</p>
          </div>
        )}

        <Block icon={Info} title="Overview" accent={T.accent}>{b.what_this_is}</Block>
        <Block icon={Target} title="Why it matters" accent={T.accent}>{b.why_it_matters}</Block>

        {b.what_to_keep_in_mind?.length > 0 && (
          <Section icon={BookOpen} title="What to keep in mind" accent={T.accent}>
            <ul className="space-y-2.5">
              {b.what_to_keep_in_mind.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-nm-ink/85 leading-relaxed">
                  <span className={`mt-2 h-1.5 w-1.5 rounded-full ${T.dot} shrink-0`} />{p}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {b.what_to_do?.length > 0 && (
          <Section icon={ListChecks} title="What to do" accent={T.accent}>
            <ol className="space-y-3">
              {b.what_to_do.map((p, i) => (
                <li key={i} className="flex items-start gap-4 leading-relaxed">
                  <span className={`font-serif text-lg ${T.accent50} w-6 shrink-0`}>{i + 1}</span>
                  <span className="text-nm-ink/85">{p}</span>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {b.simple_approach && (
          <div className={`mt-8 rounded-2xl border ${T.simpleBox} p-6`} data-testid="simple-approach">
            <div className="flex items-center gap-2 text-nm-ink font-medium"><Lightbulb size={17} className={T.simpleIcon} /> A simple way to approach it</div>
            <p className="text-nm-ink/80 leading-relaxed mt-2">{b.simple_approach}</p>
          </div>
        )}

        {b.example_preview && (
          <Section icon={FileText} title="Completed example" accent={T.accent}>
            <div className={`rounded-2xl border ${T.tint} p-6 text-nm-ink/85 leading-relaxed italic`}>{b.example_preview}</div>
          </Section>
        )}

        {b.common_mistakes?.length > 0 && (
          <Section icon={AlertTriangle} title="Common mistakes" accent={T.accent}>
            <ul className="space-y-2.5">
              {b.common_mistakes.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-nm-ink/85 leading-relaxed">
                  <AlertTriangle size={15} className="text-nm-coral shrink-0 mt-1" />{p}
                </li>
              ))}
            </ul>
          </Section>
        )}
        </>)}

        {/* Complete toggle */}
        <div className={`mt-12 flex flex-wrap items-center gap-4 border-t ${T.border10} pt-8`}>
          <button onClick={toggleComplete} data-testid="mark-complete-btn"
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-colors duration-200 ${completed ? "bg-nm-green text-white" : `${T.primaryBtn} text-white hover:brightness-105`}`}>
            {completed ? <><CheckCircle2 size={18} /> Completed</> : <><Circle size={18} /> Mark complete</>}
          </button>
          <Link to={`/app/part/${ch.part}`} className={`text-sm ${T.accent} hover:underline`}>Back to {ch.section_title}</Link>
        </div>
      </article>

      {/* Right rail */}
      <aside className="space-y-6">
        {b.tool_preview && ch.related_templates?.length > 0 && (
          <div className="rounded-2xl border border-nm-green/20 bg-nm-green/[0.05] p-5" data-testid="tool-preview">
            <Overline className="text-nm-green">Practical tool</Overline>
            <p className="text-sm text-nm-ink/80 mt-2 leading-relaxed">{b.tool_preview}</p>
          </div>
        )}
        {ch.related_templates?.length > 0 && (
          <div>
            <div className="text-sm font-medium text-nm-ink mb-3">Chapter PDFs</div>
            <div className="space-y-2.5">
              {ch.related_templates.map((t) => (
                <button key={t.slug} onClick={() => handleDownload(t)} disabled={busy === t.slug}
                  data-testid={`download-tpl-${t.slug}`}
                  className={`group w-full flex items-center gap-3 rounded-xl border ${T.borderSoft} bg-nm-surface p-3.5 text-left ${T.borderHover} transition-colors duration-200 disabled:opacity-60`}>
                  <FileText size={16} className="text-nm-green shrink-0" />
                  <span className="text-sm text-nm-ink flex-1 leading-snug">{busy === t.slug ? "Preparing…" : t.title}</span>
                  <Download size={14} className={`${T.accent50} group-hover:translate-y-0.5 transition-transform duration-200`} />
                </button>
              ))}
            </div>
          </div>
        )}
        {ch.next_chapter && (
          <div>
            <div className="text-sm font-medium text-nm-ink mb-3">Next chapter</div>
            <Link to={`/app/chapter/${ch.next_chapter.slug}`} data-testid="next-chapter"
              className={`group flex items-center gap-3 rounded-xl border ${T.borderSoft} bg-nm-surface p-3.5 ${T.borderHover} transition-colors duration-200`}>
              <span className="text-sm text-nm-ink flex-1 leading-snug">{ch.next_chapter.title}</span>
              <ArrowRight size={14} className={`${T.accent50} group-hover:translate-x-0.5 transition-transform duration-200`} />
            </Link>
          </div>
        )}
        {ch.related_chapters?.length > 0 && (
          <div>
            <div className="text-sm font-medium text-nm-ink mb-3">Related chapters</div>
            <div className="space-y-2.5">
              {ch.related_chapters.map((c) => (
                <Link key={c.slug} to={`/app/chapter/${c.slug}`} data-testid={`related-ch-${c.slug}`}
                  className={`group flex items-center gap-3 rounded-xl border ${T.borderSoft} bg-nm-surface p-3.5 ${T.borderHover} transition-colors duration-200`}>
                  <span className="text-sm text-nm-ink flex-1 leading-snug">{c.title}</span>
                  <ArrowRight size={14} className={`${T.accent50} group-hover:translate-x-0.5 transition-transform duration-200`} />
                </Link>
              ))}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

function BackLink({ part }) {
  const hover = part === "part_2" ? "hover:text-nm-plum" : "hover:text-nm-primary";
  return (
    <Link to={`/app/part/${part}`} className={`inline-flex items-center gap-1.5 text-sm text-nm-neutral ${hover} mb-4 transition-colors`} data-testid="chapter-back">
      <ArrowLeft size={15} /> Back
    </Link>
  );
}

function Block({ icon: Icon, title, children, accent = "text-nm-primary" }) {
  return (
    <div className="mt-8">
      <div className={`flex items-center gap-2 ${accent} font-medium text-sm uppercase tracking-wide`}><Icon size={16} /> {title}</div>
      <p className="text-nm-ink/85 leading-relaxed mt-2.5 text-[17px]">{children}</p>
    </div>
  );
}

function Section({ icon: Icon, title, children, accent = "text-nm-primary" }) {
  return (
    <div className="mt-8">
      <div className={`flex items-center gap-2 ${accent} font-medium text-sm uppercase tracking-wide mb-3`}><Icon size={16} /> {title}</div>
      {children}
    </div>
  );
}

